import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AI_INFRASTRUCTURE_STOCK_SERIES } from "@/data/aiInfrastructureStockSeries";

type AIInfrastructureStockChartProps = {
  slug: string;
};

function formatPrice(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string) {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

export function AIInfrastructureStockChart({ slug }: AIInfrastructureStockChartProps) {
  const series = AI_INFRASTRUCTURE_STOCK_SERIES[slug];
  if (!series) return null;

  const start = series.points[0];
  const end = series.points[series.points.length - 1];
  const direction = end.close >= start.close ? "up" : "down";
  const change = ((end.close - start.close) / start.close) * 100;
  const dates = [
    series.points[0]?.date,
    series.points[Math.floor(series.points.length / 2)]?.date,
    series.points[series.points.length - 1]?.date,
  ].filter(Boolean);

  return (
    <section
      aria-labelledby={`${series.symbol.toLowerCase()}-market-context-heading`}
      className="not-prose container mx-auto mb-12 max-w-4xl px-4"
    >
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <div className="border-b border-border bg-muted/30 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#008f78] dark:text-[#5ee6cb]">
                Market context, not investment advice
              </p>
              <h2 id={`${series.symbol.toLowerCase()}-market-context-heading`} className="mt-1 font-serif text-xl text-foreground">
                Three-month share-price context: {series.company} ({series.symbol})
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-muted-foreground sm:text-right">
              Daily close: <span className="font-semibold text-foreground">{formatPrice(end.close)}</span> on {formatDate(end.date)}. {series.symbol} moved {direction} {Math.abs(change).toFixed(1)}% across this displayed period.
            </p>
          </div>
        </div>

        <div className="h-[280px] px-3 pb-2 pt-5 sm:h-[320px] sm:px-5">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series.points} margin={{ top: 10, right: 16, bottom: 8, left: 4 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                tickFormatter={formatDate}
                ticks={dates}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                tickFormatter={(value) => `$${value}`}
                width={54}
                domain={["dataMin - 2", "dataMax + 2"]}
              />
              <Tooltip
                cursor={{ stroke: "hsl(var(--muted-foreground))", strokeDasharray: "4 4" }}
                contentStyle={{
                  borderRadius: "12px",
                  border: "1px solid hsl(var(--border))",
                  background: "hsl(var(--card))",
                  color: "hsl(var(--foreground))",
                }}
                labelFormatter={(label) => formatDate(String(label))}
                formatter={(value) => [formatPrice(Number(value)), "Close"]}
              />
              <Line
                type="monotone"
                dataKey="close"
                stroke={direction === "up" ? "#00A98F" : "#D85A3A"}
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="border-t border-border px-5 py-4 text-xs leading-relaxed text-muted-foreground sm:px-6">
          <p>
            <strong className="font-semibold text-foreground">Method:</strong> daily regular-market closes supplied by Yahoo Finance’s stock-chart data on {series.asOf}. A chart records historical movement; it does not estimate value, predict returns or recommend buying, selling or holding a security.
          </p>
          <p className="mt-1.5">
            The article’s reporting and source links explain the relevant business development. Market data can move after retrieval and should be checked against a current, independent market source before any financial decision.
          </p>
        </div>
      </div>
    </section>
  );
}
